import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Spinner, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import AwardVotingApi, {
  AwardCurrentBallot,
  AwardVotingPeriod,
} from "../../Api/AwardVotingApi";

function formatDeadline(iso?: string) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const AwardsVotingPage: React.FC = () => {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const [ballot, setBallot] = useState<AwardCurrentBallot | null>(null);
  const [openPeriods, setOpenPeriods] = useState<AwardVotingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [submittingUserId, setSubmittingUserId] = useState<number | null>(null);

  const loadBallot = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const res = await AwardVotingApi.getBallot(id);
      setBallot(res.data);
      setOpenPeriods([]);
    } catch {
      toast.error("Unable to load awards ballot.");
      setBallot(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = Number(periodId);
    if (periodId && Number.isFinite(id) && id > 0) {
      loadBallot(id);
      return;
    }

    let active = true;
    setLoading(true);
    AwardVotingApi.getOpenPeriods()
      .then((res) => {
        if (!active) return;
        const periods = res.data ?? [];
        if (periods.length === 1) {
          navigate(`/members/awards/vote/${periods[0].id}`, { replace: true });
          return;
        }
        setOpenPeriods(periods);
        setBallot(null);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        toast.error("Unable to load awards voting.");
        setOpenPeriods([]);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [periodId, loadBallot, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const candidates = ballot?.candidates ?? [];
    if (!q) return candidates;
    return candidates.filter((c) =>
      `${c.name} ${c.memberName ?? ""}`.toLowerCase().includes(q)
    );
  }, [ballot, search]);

  const castVote = async (userId: number) => {
    if (!ballot?.period?.id || !ballot.canVote) return;
    setSubmittingUserId(userId);
    try {
      const res = await AwardVotingApi.castVote(ballot.period.id, userId);
      if (!res.data.success) {
        toast.error(res.data.message || "Unable to record vote.");
        return;
      }
      toast.success(ballot.hasVoted ? "Your vote was updated." : "Your vote has been recorded.");
      await loadBallot(ballot.period.id);
    } catch {
      toast.error("Unable to record vote.");
    } finally {
      setSubmittingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!periodId) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <h1 className="text-3xl font-bold text-gray-900">Awards Voting</h1>
        {openPeriods.length === 0 ? (
          <p className="mt-4 text-gray-600">No awards voting period is open right now.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {openPeriods.map((period) => (
              <li key={period.id} className="rounded-lg border border-gray-200 bg-white p-4">
                <Link
                  to={`/members/awards/vote/${period.id}`}
                  className="text-lg font-semibold text-blue-700 hover:underline"
                >
                  {period.title}
                </Link>
                <p className="mt-1 text-sm text-gray-600">
                  {period.subscriptionName || "Membership type"} · open until {formatDeadline(period.endsUtc)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const myVoteId = ballot?.myVote?.userId;

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6">
      <h1 className="text-3xl font-bold text-gray-900">
        {ballot?.period?.title || "Awards Voting"}
      </h1>
      {ballot?.period && (
        <p className="mt-1 text-gray-600">
          {ballot.period.year}
          {ballot.period.subscriptionName ? ` · ${ballot.period.subscriptionName}` : ""}
          {ballot.period.isOpen ? ` · open until ${formatDeadline(ballot.period.endsUtc)}` : ""}
        </p>
      )}

      {ballot?.message && (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {ballot.message}
        </p>
      )}

      {ballot?.winners && ballot.winners.length > 0 && (
        <div className="mt-4 rounded-md bg-green-50 px-3 py-3 text-green-900">
          <p className="font-semibold">
            {ballot.winners.length > 1 ? "Winners" : "Winner"}
          </p>
          <ul className="mt-1 space-y-1">
            {ballot.winners.map((winner) => (
              <li key={winner.userId || `member-${winner.memberId}`}>
                {winner.name}
                {winner.memberName && winner.memberName !== winner.name
                  ? ` · ${winner.memberName}`
                  : ""}{" "}
                ({winner.voteCount} {winner.voteCount === 1 ? "vote" : "votes"})
              </li>
            ))}
          </ul>
        </div>
      )}

      {ballot?.myVote && (
        <p className="mt-4 text-sm text-gray-700">
          Your current vote: <strong>{ballot.myVote.name}</strong>
          {ballot.myVote.memberName && ballot.myVote.memberName !== ballot.myVote.name
            ? ` (${ballot.myVote.memberName})`
            : ""}
          {ballot.canVote ? " (you can change it until voting closes)" : ""}
        </p>
      )}

      {ballot?.canVote && !ballot.hasVoted && (
        <p className="mt-4 text-sm text-gray-500">Select a person below to cast your vote.</p>
      )}

      {(ballot?.candidates?.length ?? 0) > 0 && (
        <div className="mt-4">
          <TextInput
            placeholder="Search people"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      <ul className="mt-4 space-y-2">
        {filtered.map((candidate) => {
          const selected = candidate.userId === myVoteId;
          return (
            <li
              key={candidate.userId}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                {candidate.logoUrl ? (
                  <img
                    src={candidate.logoUrl}
                    alt=""
                    className="h-10 w-10 rounded object-contain bg-gray-50"
                  />
                ) : (
                  <div className="h-10 w-10 rounded bg-gray-100" />
                )}
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">{candidate.name}</span>
                  {candidate.memberName ? (
                    <span className="block truncate text-xs text-gray-500">{candidate.memberName}</span>
                  ) : null}
                </span>
              </div>
              {ballot?.canVote && (
                <Button
                  size="sm"
                  color={selected ? "success" : "blue"}
                  disabled={selected || submittingUserId != null}
                  onClick={() => castVote(candidate.userId)}
                >
                  {submittingUserId === candidate.userId
                    ? "Saving..."
                    : selected
                      ? "Voted"
                      : "Vote"}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {ballot && (ballot.candidates?.length ?? 0) === 0 && !ballot.message && (
        <p className="mt-4 text-gray-600">No people are available for this ballot.</p>
      )}
      {filtered.length === 0 && (ballot?.candidates?.length ?? 0) > 0 && (
        <p className="mt-4 text-sm text-gray-500">No people match that search.</p>
      )}
    </div>
  );
};

export default AwardsVotingPage;
